import React, { FunctionComponent, ReactNode, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { ReactComponent as IconMove } from "../../../../../icons/move.svg";
import "./style.scss";

interface Props {
  children: ReactNode;
  index: number;
  onDragEnd: (pageKey: string) => void;
  onDragOver: (pageKey: string, position: "before" | "after") => void;
  onDragStart: (
    pageKey: string,
    movableElement: HTMLDivElement,
    refEvent: { pageX: number; pageY: number }
  ) => void;
  pageKey: string;
}

const type = "DraggablePage";

const DraggablePage: FunctionComponent<Props> = ({
  children,
  index,
  onDragEnd,
  onDragOver,
  onDragStart,
  pageKey,
}) => {
  const pageRef = useRef<HTMLDivElement>(null);

  const getDroppablePosition = (pageX: number): "before" | "after" | null => {
    if (pageRef === null) {
      return null;
    }
    const currentPage = pageRef.current as HTMLElement;
    const targetBoundingRect = currentPage.getBoundingClientRect();
    const mousePos =
      pageX - targetBoundingRect.left < targetBoundingRect.width / 2 ? "before" : "after";
    return mousePos;
  };

  const [, drop] = useDrop({
    accept: type,
    hover(item, targetMonitor) {
      if (Date.now() % 50 > 0) {
        return;
      }

      const coordinates = targetMonitor.getClientOffset();
      if (!coordinates) {
        return;
      }
      const position = getDroppablePosition(coordinates.x);
      if (!position) {
        return;
      }
      onDragOver(pageKey, position);
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    begin: (monitor) => {
      const itemPosition = monitor.getClientOffset();
      if (itemPosition === null || pageRef.current === null) {
        return;
      }
      const pageElement = pageRef.current as HTMLElement;
      const movableElement = pageElement.cloneNode(true) as HTMLDivElement;
      movableElement.className += " EditablePageList-item-draggable-shadow";
      onDragStart(pageKey, movableElement, {
        pageX: itemPosition.x,
        pageY: itemPosition.y,
      });
      movableElement.style.visibility = "hidden";
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDragEnd(pageKey);
    },
    item: { type, id: pageKey, index },
  });

  preview(getEmptyImage());

  drag(drop(pageRef));

  return (
    <>
      <div className="DraggablePage" style={{ opacity: isDragging ? 0 : 1 }} ref={pageRef}>
        <div className="EditablePageList-item-draggable--hovered">
          <div className="icon">
            <IconMove />
          </div>
        </div>
        {children}
      </div>
    </>
  );
};

export default DraggablePage;
